import { Injectable } from '@angular/core';
import { ApModalComponentType } from 'webcommon/core-ui';
import { FindControlIdService } from 'webcommon/legacy-common';

import { FcItemModalContext, ModalComponentTypeWithSize, ModalSizeEnum, ModalSizeType } from '../models/index';
import {
  FcAnsiListModalComponent,
  FcCorporationListModalComponent,
  FcEmployerListModalComponent,
  FcFacilityPortalPersonListModalComponent,
  FcGenericListModalComponent,
  FcIndustryListModalComponent,
  FcInsuranceContactListModalComponent,
  FcInsurancePayerListModalComponent,
  FcInsurancePlanListModalComponent,
  FcJobScheduleListModalComponent,
  FcLaboratoryBillCodeListModalComponent,
  FcLanguageListModalComponent,
  FcMacroGroupListModalComponent,
  FcMedicalServiceProviderListModalComponent,
  FcOccupationListModalComponent,
  FcPatientConditionListModalComponent,
  FcPatientProgramListModalComponent,
  FcPersonWebAccountListModalComponent,
  FcPharmacyClearinghouseModalComponent,
  FcPharmacyListModalComponent,
  FcPhoneTypeListModalComponent,
  FcResponsiblePartyListModalComponent,
  FcScheduledReportListModalComponent,
  FcVisitCheckListModalComponent,
  FcZipListModalComponent
} from './fc-item-modals/index';
import { FcItemModalBaseComponent } from './generic/index';

@Injectable({
  providedIn: 'root',
})
export class FcItemConfig {
  private readonly fcItemModalById = new Map<string, ModalComponentTypeWithSize<FcItemModalBaseComponent>>();
  private readonly defaultItemModal: ModalComponentTypeWithSize<FcItemModalBaseComponent> = {
    component: FcGenericListModalComponent,
    modalSize: ModalSizeEnum.small,
  };

  constructor() {
    this.registerItemModal(FindControlIdService.ListIndustry, FcIndustryListModalComponent);
    this.registerItemModal(FindControlIdService.ListPhoneType, FcPhoneTypeListModalComponent);
    this.registerItemModal(FindControlIdService.ListAmendmentSource, FcGenericListModalComponent);
    this.registerItemModal(FindControlIdService.ListHistorian, FcGenericListModalComponent);
    this.registerItemModal(FindControlIdService.ListMaritalStatus, FcAnsiListModalComponent);
    this.registerItemModal(FindControlIdService.ListEmploymentStatus, FcAnsiListModalComponent);
    this.registerItemModal(FindControlIdService.ListEthnicity, FcAnsiListModalComponent);
    this.registerItemModal(FindControlIdService.ListGender, FcAnsiListModalComponent);
    this.registerItemModal(FindControlIdService.ListGenderIdentity, FcAnsiListModalComponent);
    this.registerItemModal(FindControlIdService.ListOccupation, FcOccupationListModalComponent);
    this.registerItemModal(FindControlIdService.ListLanguage, FcLanguageListModalComponent);
    this.registerItemModal(FindControlIdService.ListPatientCondition, FcPatientConditionListModalComponent);
    this.registerItemModal(FindControlIdService.ViewPersonWeb, FcPersonWebAccountListModalComponent);
    this.registerItemModal(
      FindControlIdService.ViewPharmacy,
      FcPharmacyListModalComponent,
      ModalSizeEnum.medium,
    );
    this.registerItemModal(
      FindControlIdService.ListPharmacyClearinghouse,
      FcPharmacyClearinghouseModalComponent,
      ModalSizeEnum.medium,
    );
    this.registerItemModal(FindControlIdService.ListPronoun, FcAnsiListModalComponent);
    this.registerItemModal(FindControlIdService.ListRace, FcAnsiListModalComponent);
    this.registerItemModal(FindControlIdService.ListRelationship, FcAnsiListModalComponent);
    this.registerItemModal(FindControlIdService.ListSexualOrientation, FcAnsiListModalComponent);
    this.registerItemModal(
      FindControlIdService.ViewCorporation,
      FcCorporationListModalComponent,
      ModalSizeEnum.medium,
    );
    this.registerItemModal(
      FindControlIdService.ViewInternalCorporation,
      FcCorporationListModalComponent,
      ModalSizeEnum.medium,
    );
    this.registerItemModal(
      FindControlIdService.ViewEmployer,
      FcEmployerListModalComponent,
      ModalSizeEnum.medium,
    );
    this.registerItemModal(
      FindControlIdService.ListZip,
      FcZipListModalComponent,
      ModalSizeEnum.small,
    );
    this.registerItemModal(
      FindControlIdService.ViewInsuranceCarrier,
      FcInsurancePayerListModalComponent,
      ModalSizeEnum.medium,
    );
    this.registerItemModal(
      FindControlIdService.ViewInsurancePayerContact,
      FcInsuranceContactListModalComponent,
      ModalSizeEnum.medium,
    );
    this.registerItemModal(
      FindControlIdService.ViewPayerPlan,
      FcInsurancePlanListModalComponent,
      ModalSizeEnum.medium,
    );
    this.registerItemModal(
      FindControlIdService.ListPatientProgram,
      FcPatientProgramListModalComponent,
      ModalSizeEnum.medium,
    );
    this.registerItemModal(
      FindControlIdService.ViewMedicalServicesProvider,
      FcMedicalServiceProviderListModalComponent,
      ModalSizeEnum.medium,
    );
    this.registerItemModal(
      FindControlIdService.ListLabBillCode,
      FcLaboratoryBillCodeListModalComponent,
      ModalSizeEnum.small,
    );
    this.registerItemModal(
      FindControlIdService.ViewFindFacilityPerson,
      FcFacilityPortalPersonListModalComponent,
      ModalSizeEnum.full,
    );
    this.registerItemModal(
      FindControlIdService.ViewFindPerson,
      FcFacilityPortalPersonListModalComponent,
      ModalSizeEnum.full,
    );
    this.registerItemModal(
      FindControlIdService.ViewResponsibleParty,
      FcResponsiblePartyListModalComponent,
      ModalSizeEnum.full,
    );
    this.registerItemModal(
      FindControlIdService.ListMacroGroup,
      FcMacroGroupListModalComponent,
      ModalSizeEnum.medium,
    );
    this.registerItemModal(
      FindControlIdService.JobSchedule,
      FcJobScheduleListModalComponent,
      ModalSizeEnum.medium,
    );
    this.registerItemModal(
      FindControlIdService.ScheduledReport,
      FcScheduledReportListModalComponent,
      ModalSizeEnum.medium,
    );
    this.registerItemModal(
      FindControlIdService.VisitCheckList,
      FcVisitCheckListModalComponent,
      ModalSizeEnum.medium,
    );
  }

  getItemModalTypeOrDefault(fcItemModalContext: FcItemModalContext) {
    const findControlModalType = this.fcItemModalById.get(fcItemModalContext.findControlConfig.FindControlBehavior.Id);
    return findControlModalType || this.defaultItemModal;
  }

  registerItemModal(
    findControlId: string,
    itemModalType: ApModalComponentType<FcItemModalBaseComponent>,
    modalSize: ModalSizeType = ModalSizeEnum.small,
  ) {
    this.fcItemModalById.set(findControlId, { component: itemModalType, modalSize });
  }
}
